"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import Loader from "./Loader";
import { userService } from "../services/services";
import type { MarketPriceApiItem, MarketPriceApiResponse } from "../types/domain";

type MarketItem = {
  id: string;
  productId: string;
  productName: string;
  price: string;
  change: number;
  up: boolean;
};

function generateDirection(isUp: boolean) {
  return isUp ? "↑" : "↓";
}

function calculatePriceChange(oldPrice: string, currentPrice: string) {
  const oldValue = parseFloat(oldPrice);
  const currentValue = parseFloat(currentPrice);

  if (oldValue === 0) {
    return 0;
  }

  return ((currentValue - oldValue) / oldValue) * 100;
}

function mapMarketItem(item: MarketPriceApiItem): MarketItem {
  const change = calculatePriceChange(item.oldPrice, item.currentPrice);

  return {
    id: item.id,
    productId: item.productId,
    productName: item.product?.name || "Unknown",
    price: item.currentPrice,
    change,
    up: change >= 0,
  };
}

function getPriceList(response: MarketPriceApiResponse | null | undefined) {
  if (Array.isArray(response)) {
    return response[0]?.prices ?? [];
  }

  return response?.prices ?? [];
}

function StatCard({ item }: { item: MarketItem }) {
  return (
    <div
      className="flex items-center justify-between gap-5 shrink-0 px-5 py-4 bg-[#0A1628] border-r border-white/5 transition-colors duration-200 hover:bg-[#0d1e36] cursor-default"
      style={{ minWidth: "230px" }}
    >
      <div className="flex flex-col gap-1">
        <span className="text-[20px] font-semibold text-blue-400 whitespace-nowrap capitalize">
          {item.productName}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[25px] font-bold text-white leading-none tracking-tight">
            ₹{item.price}
          </span>
        </div>
        <span className={`text-[15px] font-semibold ${item.up ? "text-[#4ADE80]" : "text-red-500"}`}>
          {item.change.toFixed(2)}%
        </span>
      </div>

      <div className="shrink-0">
        <span className={`text-[40px] font-semibold ${item.up ? "text-[#4ADE80]" : "text-red-500"}`}>
          {generateDirection(item.up)}
        </span>
      </div>
    </div>
  );
}

export default function MarketStatCards() {
  const trackRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const posRef = useRef(0);
  const pausedRef = useRef(false);
  const [marketData, setMarketData] = useState<MarketItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadMarketPrices() {
      try {
        setIsLoading(true);
        const response = await userService.getMarketPrices();
        const prices = getPriceList(response as MarketPriceApiResponse);

        if (isMounted) {
          setMarketData(prices.map(mapMarketItem));
        }
      } catch (error) {
        console.error("getMarketPrices error:", error);
        if (isMounted) {
          setMarketData([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void loadMarketPrices();

    return () => {
      isMounted = false;
    };
  }, []);

  const duplicatedData = useMemo(() => {
    if (marketData.length === 0) return [];
    const minCopies = Math.max(2, Math.ceil(20 / marketData.length));
    const evenCopies = minCopies % 2 === 0 ? minCopies : minCopies + 1;
    return Array.from({ length: evenCopies }, () => marketData).flat();
  }, [marketData]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || duplicatedData.length === 0) {
      return;
    }

    function tick() {
      const currentTrack = trackRef.current;
      if (!currentTrack) {
        return;
      }

      if (!pausedRef.current) {
        posRef.current += 0.5;
        const halfWidth = currentTrack.scrollWidth / 2;
        if (posRef.current >= halfWidth) {
          posRef.current = 0;
        }
        currentTrack.style.transform = `translateX(-${posRef.current}px)`;
      }

      animRef.current = requestAnimationFrame(tick);
    }

    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [duplicatedData]);

  return (
    <div className="w-full overflow-hidden relative min-h-[96px] bg-[#080F1C] flex items-center justify-center rounded-xl">
      {isLoading ? (
        <div className="py-2">
          <Loader />
        </div>
      ) : (
        <>
          <div
            className="absolute left-0 top-0 bottom-0 w-20 z-10 pointer-events-none"
            style={{ background: "linear-gradient(90deg, #080F1C 0%, transparent 100%)" }}
          />
          <div
            className="absolute right-0 top-0 bottom-0 w-20 z-10 pointer-events-none"
            style={{ background: "linear-gradient(270deg, #080F1C 0%, transparent 100%)" }}
          />

          <div
            ref={trackRef}
            className="flex gap-px will-change-transform"
            onMouseEnter={() => {
              pausedRef.current = true;
            }}
            onMouseLeave={() => {
              pausedRef.current = false;
            }}
          >
            {duplicatedData.map((item, index) => (
              <StatCard key={`${item.id}-${index}`} item={item} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
